import React, { useState, useEffect } from 'react';
import { Megaphone, Link as LinkIcon, Plus, Info, AlertTriangle, X, Trash2 } from 'lucide-react';
import { toast } from '../components/Toast';
import { GoalMode, Environment, ConnectionConfig, Campaign } from '../types';
import EmptyState from '../components/EmptyState';

interface CampaignsProps {
  goalMode: GoalMode;
  activeEnv: Environment | null;
}

const Campaigns: React.FC<CampaignsProps> = ({ goalMode, activeEnv }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    objective: '',
    audience: '',
    start_date: '',
    end_date: '',
    channels: [] as string[],
    landing_pages: [''],
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    owner: ''
  });

  // Load campaigns when environment changes
  useEffect(() => {
    if (!activeEnv || activeEnv.isSample) {
      setCampaigns([]);
      return;
    }

    const loadCampaigns = async () => {
      try {
        const response = await fetch(`/api/campaigns?environmentId=${activeEnv.id}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setCampaigns(data.campaigns || []);
        }
      } catch (error) {
        console.error('Failed to load campaigns:', error);
      }
    };

    loadCampaigns();
  }, [activeEnv]);

  const handleCreate = async () => {
    if (!activeEnv || !formData.name) return;

    setLoading(true);
    try {
      const response = await fetch('/api/campaigns', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environmentId: activeEnv.id,
          ...formData,
          kpi_targets: {}
        })
      });

      if (response.ok) {
        const newCampaign = await response.json();
        setCampaigns([...campaigns, newCampaign]);
        setShowModal(false);
        setFormData({
          name: '',
          objective: '',
          audience: '',
          start_date: '',
          end_date: '',
          channels: [],
          landing_pages: [''],
          utm_source: '',
          utm_medium: '',
          utm_campaign: '',
          owner: ''
        });
      } else {
        toast.error('Failed to create campaign');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this campaign?')) return;

    try {
      const response = await fetch(`/api/campaigns?id=${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setCampaigns(campaigns.filter(c => c.id !== id));
      } else {
        toast.error('Failed to delete campaign');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  if (!activeEnv) return <EmptyState />;

  const hasAnyData = (Object.values(activeEnv.connections) as ConnectionConfig[]).some(v => v?.connected === true);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Campaign Management</h2>
          <p className="text-slate-500 mt-1 flex items-center gap-2 text-sm italic">
            Full-cycle campaign tracking and performance analysis.
            <Info size={14} className="text-slate-400" title="Connects UTM parameters to GA4 conversion events for ROI analysis." />
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          disabled={activeEnv.isSample}
          className="flex items-center gap-2 px-4 py-2 bg-[#003B71] text-white rounded-lg font-bold hover:bg-[#002b51] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={18} /> New Campaign
        </button>
      </div>

      {!hasAnyData && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-center gap-4 text-amber-800">
          <AlertTriangle className="flex-shrink-0" />
          <div className="text-sm">
            <p className="font-bold">Campaign tracking inactive.</p>
            <p className="opacity-80">Connect GA4 or UTM monitoring tools in <a href="#/settings" className="underline font-bold">Settings</a> to track performance.</p>
          </div>
        </div>
      )}

      {campaigns.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-12 text-center">
          <Megaphone size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-bold text-slate-900 mb-2">No campaigns yet</h3>
          <p className="text-slate-500 text-sm mb-6">Create your first campaign to start tracking performance</p>
          <button
            onClick={() => setShowModal(true)}
            disabled={activeEnv.isSample}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#003B71] text-white rounded-lg font-bold hover:bg-[#002b51] transition-colors disabled:opacity-50"
          >
            <Plus size={18} /> New Campaign
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {campaigns.map(campaign => (
            <div key={campaign.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden group">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                      <Megaphone size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">{campaign.name}</h3>
                      <p className="text-xs text-slate-400">Active: {campaign.start_date} - {campaign.end_date}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(campaign.id)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  <p><span className="font-bold text-slate-600">Objective:</span> {campaign.objective}</p>
                  <p><span className="font-bold text-slate-600">Audience:</span> {campaign.audience}</p>
                  <p><span className="font-bold text-slate-600">Channels:</span> {campaign.channels.join(', ')}</p>
                  {campaign.utm_schema && (
                    <p className="font-mono text-xs text-slate-500">
                      UTM: {campaign.utm_schema.source} / {campaign.utm_schema.medium} / {campaign.utm_schema.campaign}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black text-slate-900">New Campaign</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-2">Campaign Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FFCD00] outline-none"
                  placeholder="Fall 2025 PhD Recruitment"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-2">Objective *</label>
                <input
                  type="text"
                  value={formData.objective}
                  onChange={e => setFormData({ ...formData, objective: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FFCD00] outline-none"
                  placeholder="Increase PhD applications"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-2">Target Audience *</label>
                <input
                  type="text"
                  value={formData.audience}
                  onChange={e => setFormData({ ...formData, audience: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FFCD00] outline-none"
                  placeholder="Graduate Applicants"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FFCD00] outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-2">End Date *</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={e => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FFCD00] outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-2">Owner</label>
                <input
                  type="text"
                  value={formData.owner}
                  onChange={e => setFormData({ ...formData, owner: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FFCD00] outline-none"
                  placeholder="Your name"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-2">UTM Source</label>
                  <input
                    type="text"
                    value={formData.utm_source}
                    onChange={e => setFormData({ ...formData, utm_source: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FFCD00] outline-none"
                    placeholder="linkedin"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-2">UTM Medium</label>
                  <input
                    type="text"
                    value={formData.utm_medium}
                    onChange={e => setFormData({ ...formData, utm_medium: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FFCD00] outline-none"
                    placeholder="social"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-2">UTM Campaign</label>
                  <input
                    type="text"
                    value={formData.utm_campaign}
                    onChange={e => setFormData({ ...formData, utm_campaign: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FFCD00] outline-none"
                    placeholder="fall-25-phd"
                  />
                </div>
              </div>

              <button
                onClick={handleCreate}
                disabled={loading || !formData.name || !formData.objective}
                className="w-full py-3 bg-[#0B2341] text-white rounded-2xl font-black shadow-xl hover:shadow-[#0B2341]/20 hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;